import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Users, Search, Mail, Phone } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../utils/api';
import { toast } from 'sonner';

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', title: '', email: '', phone: '', organization_id: '', is_primary: false
  });

  useEffect(() => {
    Promise.all([
      api.get('/contacts'),
      api.get('/organizations')
    ]).then(([contactsRes, orgsRes]) => {
      setContacts(contactsRes.data);
      setOrganizations(orgsRes.data);
    }).catch(() => toast.error('Failed to load data')).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/contacts', formData);
      toast.success('Contact created');
      setDialogOpen(false);
      setFormData({ first_name: '', last_name: '', title: '', email: '', phone: '', organization_id: '', is_primary: false });
      const res = await api.get('/contacts');
      setContacts(res.data);
    } catch (error) {
      toast.error('Failed to create contact');
    }
  };

  const filteredContacts = contacts.filter(c =>
    `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="flex items-center justify-center h-96"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="space-y-6" data-testid="contacts-page">
      <div className="flex justify-between items-center">
        <div><h1 className="text-4xl font-bold text-gray-900">Contacts</h1><p className="text-gray-600">Manage contact persons</p></div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild><Button className="bg-blue-600" data-testid="add-contact-button"><Plus className="w-4 h-4 mr-2" />Add Contact</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Contact</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>First Name *</Label><Input value={formData.first_name} onChange={(e) => setFormData({...formData, first_name: e.target.value})} required data-testid="contact-first-name" /></div>
              <div><Label>Last Name *</Label><Input value={formData.last_name} onChange={(e) => setFormData({...formData, last_name: e.target.value})} required data-testid="contact-last-name" /></div>
              <div><Label>Title</Label><Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} data-testid="contact-title" /></div>
              <div><Label>Email *</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required data-testid="contact-email" /></div>
              <div><Label>Phone</Label><Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="contact-phone" /></div>
              <div><Label>Organization *</Label>
                <Select value={formData.organization_id} onValueChange={(v) => setFormData({...formData, organization_id: v})} required>
                  <SelectTrigger data-testid="contact-org-select"><SelectValue placeholder="Select organization" /></SelectTrigger>
                  <SelectContent>{organizations.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-blue-600" data-testid="contact-submit">Create Contact</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><div className="flex items-center space-x-2"><Search className="w-5 h-5 text-gray-400" /><Input placeholder="Search contacts..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="max-w-sm" data-testid="search-contacts" /></div></CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredContacts.map(contact => {
              const org = organizations.find(o => o.id === contact.organization_id);
              return (
                <Card key={contact.id} className="card-hover" data-testid={`contact-card-${contact.id}`}>
                  <CardContent className="p-6">
                    <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center mb-4">
                      <span className="text-cyan-600 font-semibold text-lg">{contact.first_name[0]}{contact.last_name[0]}</span>
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900">{contact.first_name} {contact.last_name}</h3>
                    {contact.title && <p className="text-sm text-gray-600 mb-2">{contact.title}</p>}
                    <p className="text-sm text-gray-500 mb-3">{org?.name || 'Unknown Org'}</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex items-center text-gray-600"><Mail className="w-3 h-3 mr-2" />{contact.email}</div>
                      {contact.phone && <div className="flex items-center text-gray-600"><Phone className="w-3 h-3 mr-2" />{contact.phone}</div>}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
